import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gift, Copy, Check, Users, Rocket, Share2, Handshake } from "lucide-react";

export default function InvitePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const link = data ? `${window.location.origin}/register?ref=${data.code}` : "";

  useEffect(() => {
    if (user) api.get("/referral/me").then(({ data }) => setData(data)).catch(() => {});
  }, [user]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy it manually");
    }
  };

  const share = async () => {
    const payload = {
      title: "Hustler's Hall",
      text: "I'm on Hustler's Hall — a marketplace to fund and flip real money-making deals. Join me:",
      url: link,
    };
    if (navigator.share) {
      try { await navigator.share(payload); } catch { /* cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Gift className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white">Invite hustlers</h1>
            <p className="text-sm text-slate-400">Bring people with real deals. Every hustler you vouch in builds your name on the Hall.</p>
          </div>
        </div>

        {/* Share card */}
        <section className="rounded-xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-5 space-y-4" data-testid="invite-link-card">
          <div className="flex items-center gap-2 text-white font-semibold">
            <Share2 className="h-4 w-4 text-amber-400" /> Your personal invite link
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="invite-link-input"
              readOnly
              value={link}
              onClick={(e) => e.target.select()}
              className="bg-[#0B0E14] border-white/10 text-white font-mono text-sm focus-visible:ring-amber-500"
            />
            <div className="flex gap-2">
              <Button data-testid="invite-copy-btn" onClick={copy} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10 px-4 shrink-0">
                {copied ? <><Check className="h-4 w-4 mr-1.5" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
              </Button>
              <Button data-testid="invite-share-btn" onClick={share} variant="outline" className="rounded-full h-10 px-4 border-white/15 bg-white/5 text-white hover:bg-white/10 shrink-0">
                <Share2 className="h-4 w-4 mr-1.5" /> Share
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Anyone who signs up through your link is tracked to you. When they post their first real deal, it counts toward your record below.
          </p>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 gap-4">
          <div className="rounded-xl bg-[#121721] border border-white/10 p-5" data-testid="invite-stat-joined">
            <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider"><Users className="h-4 w-4" /> Hustlers joined</div>
            <div className="text-3xl font-mono font-bold text-white mt-2">{data?.invited_count ?? 0}</div>
          </div>
          <div className="rounded-xl bg-[#121721] border border-white/10 p-5" data-testid="invite-stat-deals">
            <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider"><Rocket className="h-4 w-4" /> Posted a real deal</div>
            <div className="text-3xl font-mono font-bold text-amber-400 mt-2">{data?.successful_invites ?? 0}</div>
          </div>
        </section>

        {/* Invited list */}
        <section className="rounded-xl bg-[#121721] border border-white/10 overflow-hidden" data-testid="invite-list">
          <div className="px-5 py-3 text-sm font-semibold text-white border-b border-white/10 flex items-center gap-2">
            <Handshake className="h-4 w-4 text-amber-400" /> People you've brought in
          </div>
          {(data?.invited || []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">
              No one yet. Share your link to start building your crew.
            </div>
          ) : (
            data.invited.map((i, idx) => (
              <div key={idx} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0 text-sm">
                <span className="text-slate-200 truncate">{i.name}</span>
                {i.posted_deal ? (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-400 font-semibold shrink-0 ml-3">
                    <Rocket className="h-3.5 w-3.5" /> Posted a deal
                  </span>
                ) : (
                  <span className="text-xs text-slate-500 shrink-0 ml-3">Joined</span>
                )}
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}
